export type ConnectionPhase =
  | "unsupported"
  | "disconnected"
  | "connecting"
  | "reconnecting"
  | "syncing"
  | "connected"
  | "error";

export type HeadId = 1 | 2;
export type HeadPhase = "idle" | "prewet" | "extracting" | "complete" | "demo";
export type HeadStatus = "last" | "analyzing" | "prewet" | "live" | "flush_ignored" | "incomplete" | "sensor_fault" | "storage_error";
export type CommandResultCode =
  | "ok"
  | "queued"
  | "busy"
  | "invalid"
  | "not_found"
  | "conflict"
  | "storage_unavailable"
  | "confirmation_required"
  | "persistence_pending"
  | "unauthorized";

export interface TracePoint {
  elapsedMs: number;
  milliliters: number;
}

export interface Trace {
  points: TracePoint[];
  plateauStartMs?: number;
  plateauEndMs?: number;
  classification?: string;
  reasonFlags?: number;
}

export interface HeadState {
  id: HeadId;
  phase: HeadPhase;
  status: HeadStatus;
  reasonFlags: number;
  detectorActive: boolean;
  demoActive: boolean;
  saveAvailable: boolean;
  volumeMl: number;
  elapsedMs: number;
  score?: number;
  trace: Trace;
  reference?: Trace;
  pendingSave: boolean;
  liveSampleIndex?: number;
  liveTraceEpoch?: number;
  liveTotalTicks?: number;
  liveConnectionResetPending?: boolean;
  wireTraceKind?: number;
}

export interface SnapshotSummary {
  id: number;
  name: string;
  capturedShotNumber: number;
  volumeMl: number;
  durationMs: number;
  sampleCount: number;
  classification: string;
  score?: number;
  active: boolean;
}

export type SettingGroup = "calibration" | "detection" | "prewet" | "extraction" | "display" | "developer";
export type SettingKind = "number" | "boolean";

export interface SettingDescriptor {
  id: number;
  key: string;
  label: string;
  help: string;
  unit: string;
  group: SettingGroup;
  kind: SettingKind;
  value: number | boolean;
  pendingValue?: number | boolean;
  defaultValue: number | boolean;
  min?: number;
  max?: number;
  step?: number;
}

export interface DeviceCapabilities {
  demo: boolean;
  repairStorage: boolean;
  configPatch: boolean;
  maxSnapshots: number;
}

export interface DeviceState {
  sessionId: number;
  revision: number;
  generation: number;
  deviceName: string;
  firmwareVersion: string;
  protocolVersion: number;
  storageAvailable: boolean;
  storageNeedsRepair: boolean;
  statePersistencePending: boolean;
  statePersistenceFailed: boolean;
  settingsApplyPending: boolean;
  lifetimeShots: number;
  batchShots: number;
  selectedSnapshotId?: number;
  heads: Record<HeadId, HeadState>;
  snapshots: SnapshotSummary[];
  settings: SettingDescriptor[];
  capabilities: DeviceCapabilities;
}

export interface ConfirmationRequest {
  token: bigint;
  expiresAt: number;
  title: string;
  detail: string;
  destructive: boolean;
}

export interface CommandOutcome {
  code: CommandResultCode;
  requestId?: number;
  message?: string;
  revision?: number;
  confirmation?: ConfirmationRequest;
}

export interface Notice {
  id: number;
  tone: "info" | "success" | "warning" | "error";
  text: string;
}

export const DEFAULT_SETTINGS: SettingDescriptor[] = [
  {
    id: 0,
    key: "millilitersPerTick",
    label: "Flow calibration",
    help: "Milliliters represented by one sensor edge.",
    unit: "mL / edge",
    group: "calibration",
    kind: "number",
    value: 0.25,
    defaultValue: 0.25,
    min: 0.01,
    max: 2,
    step: 0.01,
  },
  {
    id: 1,
    key: "glitchMilliseconds",
    label: "Minimum stable level",
    help: "Reject sensor pulses shorter than this interval.",
    unit: "ms",
    group: "detection",
    kind: "number",
    value: 2,
    defaultValue: 2,
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  {
    id: 2,
    key: "silenceSeconds",
    label: "End silence",
    help: "Quiet time required before completing a shot.",
    unit: "sec",
    group: "detection",
    kind: "number",
    value: 1.5,
    defaultValue: 1.5,
    min: 0.5,
    max: 10,
    step: 0.25,
  },
  {
    id: 3,
    key: "pressureDipMaximumPercent",
    label: "Pressure dip maximum",
    help: "A later trough must be at or below this share of the initial flow peak. Lower requires a deeper dip.",
    unit: "%",
    group: "detection",
    kind: "number",
    value: 35,
    defaultValue: 35,
    min: 5,
    max: 95,
    step: 1,
  },
  {
    id: 4,
    key: "prewetMinimumMl",
    label: "Prewet minimum volume",
    help: "Smallest first-segment volume held as prewet; flow shape and duration are not used.",
    unit: "mL",
    group: "prewet",
    kind: "number",
    value: 8,
    defaultValue: 8,
    min: 1,
    max: 100,
    step: 0.5,
  },
  {
    id: 5,
    key: "prewetMaximumMl",
    label: "Prewet maximum volume",
    help: "Largest first-segment volume held as prewet; flow shape and duration are not used.",
    unit: "mL",
    group: "prewet",
    kind: "number",
    value: 40,
    defaultValue: 40,
    min: 1,
    max: 100,
    step: 0.5,
  },
  {
    id: 6,
    key: "prewetMaximumGapSeconds",
    label: "Prewet wait time",
    help: "Longest pause allowed before main extraction after the first segment meets the prewet volume band.",
    unit: "sec",
    group: "prewet",
    kind: "number",
    value: 7,
    defaultValue: 7,
    min: 0.25,
    max: 20,
    step: 0.25,
  },
  {
    id: 7,
    key: "mainMinimumMl",
    label: "Main minimum volume",
    help: "Minimum volume for a valid main extraction.",
    unit: "mL",
    group: "extraction",
    kind: "number",
    value: 35,
    defaultValue: 35,
    min: 1,
    max: 250,
    step: 1,
  },
  {
    id: 8,
    key: "mainMaximumMl",
    label: "Main maximum volume",
    help: "Maximum volume for a valid main extraction.",
    unit: "mL",
    group: "extraction",
    kind: "number",
    value: 120,
    defaultValue: 120,
    min: 1,
    max: 500,
    step: 1,
  },
  {
    id: 9,
    key: "mainMinimumSeconds",
    label: "Main minimum time",
    help: "Shortest valid main extraction.",
    unit: "sec",
    group: "extraction",
    kind: "number",
    value: 5,
    defaultValue: 5,
    min: 0.5,
    max: 60,
    step: 0.5,
  },
  {
    id: 10,
    key: "mainMaximumSeconds",
    label: "Main maximum time",
    help: "Longest valid main extraction.",
    unit: "sec",
    group: "extraction",
    kind: "number",
    value: 60,
    defaultValue: 60,
    min: 0.5,
    max: 65,
    step: 0.5,
  },
  {
    id: 11,
    key: "displayTimeoutMinutes",
    label: "Display off after",
    help: "Touchscreen inactivity timeout.",
    unit: "min",
    group: "display",
    kind: "number",
    value: 5,
    defaultValue: 5,
    min: 1,
    max: 60,
    step: 1,
  },
  {
    id: 12,
    key: "showPlateauMarkers",
    label: "Plateau markers",
    help: "Show the main-extraction plateau on the controller.",
    unit: "",
    group: "display",
    kind: "boolean",
    value: true,
    defaultValue: true,
  },
  {
    id: 13,
    key: "developerMode",
    label: "Developer mode",
    help: "Enables synthetic demo shots on the controller.",
    unit: "",
    group: "developer",
    kind: "boolean",
    value: false,
    defaultValue: false,
  },
];

export function createDemoDevice(): DeviceState {
  const head = (id: HeadId): HeadState => ({
    id,
    phase: "idle",
    status: "last",
    reasonFlags: 0,
    detectorActive: false,
    demoActive: false,
    saveAvailable: false,
    volumeMl: 0,
    elapsedMs: 0,
    trace: { points: [] },
    pendingSave: false,
    wireTraceKind: 0,
  });
  return {
    sessionId: 0x9e421d,
    revision: 18,
    generation: 42,
    deviceName: "Espress9th-DEMO",
    firmwareVersion: "dev",
    protocolVersion: 2,
    storageAvailable: true,
    storageNeedsRepair: false,
    statePersistencePending: false,
    statePersistenceFailed: false,
    settingsApplyPending: false,
    lifetimeShots: 1248,
    batchShots: 16,
    selectedSnapshotId: 1001,
    heads: { 1: head(1), 2: head(2) },
    snapshots: [
      { id: 1001, name: "House / 18 g", capturedShotNumber: 1242, volumeMl: 38.4, durationMs: 28600, sampleCount: 115, classification: "SHOT", score: 94, active: true },
      { id: 1002, name: "Decaf evening", capturedShotNumber: 1247, volumeMl: 36.1, durationMs: 30100, sampleCount: 121, classification: "SHOT", score: 88, active: false },
    ],
    settings: DEFAULT_SETTINGS.map((setting) => setting.key === "developerMode" ? { ...setting, value: true } : { ...setting }),
    capabilities: { demo: true, repairStorage: true, configPatch: true, maxSnapshots: 12 },
  };
}
